/**
 * The day's numbers on the results screen (API-005, D-037): how many played today's
 * table, and how many cleared it. A shared number, not a ranking: "You beat X% of
 * players" was built and then dropped by the owner as too competitive for a daily
 * bit of fun (2026-09-26, docs/parked-ideas.md).
 *
 * The totals come from `GET /api/today/stats`, counted by the server from runs it
 * replayed itself. The player's own clear is added only AS THE SERVER COUNTED IT (the
 * send queue's last reply, daily/send.ts), never the page's (D-039).
 *
 * Everything from the server is untrusted input (CLAUDE.md §10) and checked field by
 * field; anything odd means no numbers, never wrong ones.
 */
import { DAY_NUMBERS_COPY } from '../config/site.ts';

export const DAY_STATS_URL = '/api/today/stats';

export interface DayTotals {
  readonly players: number;
  readonly cleared: number;
  /** Cleared runs by strokes, fewest first. */
  readonly strokes: readonly { readonly strokes: number; readonly count: number }[];
}

/** Far beyond any real day; a larger number is not a count we believe. */
const MAX_COUNT = 100_000_000;
const MAX_STROKES = 1000;

const wholeNumber = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

/** The server's reply for `dateKey`, if it is exactly right and adds up. */
export function readDayTotals(
  status: number,
  body: unknown,
  dateKey: string,
): DayTotals | undefined {
  if (status !== 200 || typeof body !== 'object' || body === null || Array.isArray(body)) {
    return undefined;
  }
  const b = body as Record<string, unknown>;
  if (b['dateKey'] !== dateKey) return undefined;
  const { players, cleared } = b;
  if (!wholeNumber(players, 0, MAX_COUNT) || !wholeNumber(cleared, 0, players)) return undefined;
  if (!Array.isArray(b['strokes'])) return undefined;
  const strokes: { strokes: number; count: number }[] = [];
  let total = 0;
  for (const raw of b['strokes'] as unknown[]) {
    if (typeof raw !== 'object' || raw === null) return undefined;
    const r = raw as Record<string, unknown>;
    if (!wholeNumber(r['strokes'], 1, MAX_STROKES) || !wholeNumber(r['count'], 1, MAX_COUNT)) {
      return undefined;
    }
    total += r['count'];
    strokes.push({ strokes: r['strokes'], count: r['count'] });
  }
  if (total !== cleared) return undefined;
  return { players, cleared, strokes: strokes.sort((a, b) => a.strokes - b.strokes) };
}

/**
 * The totals with this player's checked clear in them.
 *
 * The server counts once a minute, so totals fetched just after a clear usually do not
 * have it yet, and would say "0% cleared" to someone who just cleared. If nobody in
 * them has this player's strokes, this player cannot be among the clears, so they are
 * added: as a clear, and as a player only if the count of players cannot already
 * include them (they may have been counted mid-run, as not yet cleared).
 */
export function withMine(totals: DayTotals, mine: number): DayTotals {
  if (totals.strokes.some((s) => s.strokes === mine)) return totals;
  const cleared = totals.cleared + 1;
  return {
    players: Math.max(totals.players, cleared),
    cleared,
    strokes: [...totals.strokes, { strokes: mine, count: 1 }].sort((a, b) => a.strokes - b.strokes),
  };
}

/** Whole percent, rounded down: never claim more than is true. */
const percent = (share: number): string => `${String(Math.floor(share * 100))}%`;

/** The line to show: "1,284 players today · 71% cleared", or nothing before anyone plays. */
export function describeDayNumbers(counted: DayTotals, mine: number | undefined): string[] {
  const totals = mine === undefined ? counted : withMine(counted, mine);
  if (totals.players === 0) return [];
  const played = `${totals.players.toLocaleString('en')} ${
    totals.players === 1 ? DAY_NUMBERS_COPY.players[0] : DAY_NUMBERS_COPY.players[1]
  } · ${percent(totals.cleared / totals.players)} ${DAY_NUMBERS_COPY.cleared}`;
  return [played];
}

/** Fetch and check today's totals. Undefined on any trouble: the line is just absent. */
export async function fetchDayTotals(dateKey: string): Promise<DayTotals | undefined> {
  try {
    const response = await fetch(DAY_STATS_URL, { credentials: 'omit' });
    return readDayTotals(response.status, await response.json(), dateKey);
  } catch {
    return undefined;
  }
}
